import React, { useEffect, useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { BlogPost } from '../types'

const BlogDetailPageNew: React.FC = () => {
  const { slug } = useParams<{ slug: string }>()
  const navigate = useNavigate()
  const [post, setPost] = useState<BlogPost | null>(null)
  const [relatedPosts, setRelatedPosts] = useState<BlogPost[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    if (slug) {
      fetchBlogPost()
      fetchRelatedPosts()
    }
  }, [slug])

  const fetchBlogPost = async () => {
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:5000/api'
      const response = await fetch(`${apiUrl}/blog/slug/${slug}`)
      const data = await response.json()

      if (!data.success) {
        setError('Blogbeitrag nicht gefunden')
        return
      }

      setPost(data.data)
    } catch (err) {
      console.error('Error fetching blog post:', err)
      setError('Fehler beim Laden des Blogbeitrags')
    } finally {
      setLoading(false)
    }
  }

  const fetchRelatedPosts = async () => {
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:5000/api'
      const response = await fetch(`${apiUrl}/blog/related/${slug}?limit=3`)
      const data = await response.json()

      if (data.success) {
        setRelatedPosts(data.data)
      }
    } catch (err) {
      console.error('Error fetching related posts:', err)
    }
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('de-DE', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-indigo-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Lade Blogbeitrag...</p>
        </div>
      </div>
    )
  }

  if (error || !post) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4 text-gray-900">Blogbeitrag nicht gefunden</h2>
          <p className="text-gray-600 mb-8">{error || 'Der gesuchte Blogbeitrag existiert nicht.'}</p>
          <button
            onClick={() => navigate('/blog')}
            className="bg-indigo-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-indigo-700 transition"
          >
            Zur Blog-Übersicht
          </button>
        </div>
      </div>
    )
  }

  // Schema.org BlogPosting structured data
  const blogPostingSchema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": post.title,
    "description": post.excerpt,
    "image": post.featuredImage || '/images/default-blog.jpg',
    "author": {
      "@type": "Organization",
      "name": post.author
    },
    "publisher": {
      "@type": "Organization",
      "name": "PhoneMedic",
      "logo": {
        "@type": "ImageObject",
        "url": "https://phone-medic.de/logo.png"
      }
    },
    "datePublished": post.publishedAt || post.createdAt,
    "dateModified": post.updatedAt,
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://phone-medic.de/blog/${post.slug}`
    }
  }

  // Breadcrumb structured data
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://phone-medic.de"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Blog",
        "item": "https://phone-medic.de/blog"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": post.title,
        "item": `https://phone-medic.de/blog/${post.slug}`
      }
    ]
  }

  return (
    <>
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{post.seoTitle || `${post.title} | PhoneMedic Blog`}</title>
        <meta name="description" content={post.seoDescription || post.excerpt} />
        {post.seoKeywords.length > 0 && (
          <meta name="keywords" content={post.seoKeywords.join(', ')} />
        )}

        {/* Open Graph */}
        <meta property="og:title" content={post.seoTitle || post.title} />
        <meta property="og:description" content={post.seoDescription || post.excerpt} />
        <meta property="og:image" content={post.featuredImage || '/images/default-blog.jpg'} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`https://phone-medic.de/blog/${post.slug}`} />
        <meta property="article:published_time" content={post.publishedAt || post.createdAt} />
        <meta property="article:modified_time" content={post.updatedAt} />
        <meta property="article:author" content={post.author} />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.seoTitle || post.title} />
        <meta name="twitter:description" content={post.seoDescription || post.excerpt} />
        <meta name="twitter:image" content={post.featuredImage || '/images/default-blog.jpg'} />

        {/* Canonical URL */}
        <link rel="canonical" href={`https://phone-medic.de/blog/${post.slug}`} />

        {/* Structured Data */}
        <script type="application/ld+json">{JSON.stringify(blogPostingSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      <div className="min-h-screen bg-gray-50 py-8 md:py-12">
        <article className="container mx-auto px-4 max-w-4xl">

          {/* Breadcrumbs */}
          <nav className="mb-6 md:mb-8 text-sm" aria-label="Breadcrumb">
            <ol className="flex items-center flex-wrap space-x-2 text-gray-600">
              <li><Link to="/" className="hover:text-indigo-600 transition">Home</Link></li>
              <li>/</li>
              <li><Link to="/blog" className="hover:text-indigo-600 transition">Blog</Link></li>
              <li>/</li>
              <li className="text-gray-900 font-medium">{post.title}</li>
            </ol>
          </nav>

          {/* Blog Post Header */}
          <header className="mb-8">
            {/* Category */}
            <div className="mb-4">
              <span className="bg-indigo-100 text-indigo-600 px-4 py-2 rounded-full font-medium text-sm">
                {post.category}
              </span>
            </div>

            {/* Title */}
            <h1 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6">
              {post.title}
            </h1>

            {/* Meta Info */}
            <div className="flex items-center text-gray-600 text-sm space-x-4 mb-6">
              <div className="flex items-center">
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                {post.author}
              </div>
              <div className="flex items-center">
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                {formatDate(post.publishedAt || post.createdAt)}
              </div>
            </div>

            {/* Featured Image */}
            {post.featuredImage && (
              <div className="w-full rounded-lg overflow-hidden shadow-lg mb-8">
                <img
                  src={post.featuredImage}
                  alt={post.title}
                  className="w-full h-auto"
                />
              </div>
            )}
          </header>

          {/* Blog Content */}
          <div className="bg-white rounded-lg shadow-lg p-8 md:p-12 mb-12">
            {/* Custom CSS from Blog Post */}
            {post.customCss && (
              <style dangerouslySetInnerHTML={{ __html: post.customCss }} />
            )}

            <div
              className="
                prose prose-lg max-w-none
                prose-headings:font-bold prose-headings:text-gray-900 prose-headings:mb-4 prose-headings:mt-8
                prose-h1:text-4xl prose-h1:mb-6
                prose-h2:text-3xl prose-h2:border-b prose-h2:border-gray-200 prose-h2:pb-2
                prose-h3:text-2xl
                prose-h4:text-xl
                prose-p:text-gray-700 prose-p:leading-relaxed prose-p:mb-4
                prose-a:text-indigo-600 prose-a:font-medium prose-a:no-underline hover:prose-a:underline hover:prose-a:text-indigo-700
                prose-strong:text-gray-900 prose-strong:font-semibold
                prose-em:text-gray-800
                prose-ul:list-disc prose-ul:ml-6 prose-ul:mb-4
                prose-ol:list-decimal prose-ol:ml-6 prose-ol:mb-4
                prose-li:text-gray-700 prose-li:mb-2
                prose-blockquote:border-l-4 prose-blockquote:border-indigo-500 prose-blockquote:pl-4 prose-blockquote:italic prose-blockquote:text-gray-600 prose-blockquote:bg-gray-50 prose-blockquote:py-2 prose-blockquote:my-6
                prose-code:text-indigo-600 prose-code:bg-gray-100 prose-code:px-2 prose-code:py-1 prose-code:rounded prose-code:text-sm
                prose-pre:bg-gray-900 prose-pre:text-gray-100 prose-pre:p-4 prose-pre:rounded-lg prose-pre:overflow-x-auto
                prose-img:rounded-lg prose-img:shadow-md prose-img:my-8
                prose-table:border-collapse prose-table:w-full
                prose-th:bg-gray-100 prose-th:border prose-th:border-gray-300 prose-th:p-3 prose-th:text-left prose-th:font-semibold
                prose-td:border prose-td:border-gray-300 prose-td:p-3
                prose-hr:border-gray-300 prose-hr:my-8
              "
              dangerouslySetInnerHTML={{ __html: post.content }}
            />

            {/* Tags */}
            {post.tags.length > 0 && (
              <div className="mt-12 pt-8 border-t">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Tags:</h3>
                <div className="flex flex-wrap gap-2">
                  {post.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="bg-gray-100 text-gray-700 px-4 py-2 rounded-full text-sm font-medium hover:bg-gray-200 transition cursor-pointer"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Related Posts */}
          {relatedPosts.length > 0 && (
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Ähnliche Beiträge</h2>
              <div className="grid md:grid-cols-3 gap-6">
                {relatedPosts.map((relatedPost) => (
                  <Link
                    key={relatedPost._id}
                    to={`/blog/${relatedPost.slug}`}
                    className="bg-white rounded-lg shadow-md hover:shadow-xl transition-all overflow-hidden group"
                  >
                    {relatedPost.featuredImage && (
                      <div className="w-full h-40 overflow-hidden">
                        <img
                          src={relatedPost.featuredImage}
                          alt={relatedPost.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                    )}
                    <div className="p-4">
                      <h3 className="font-bold text-gray-900 mb-2 line-clamp-2 group-hover:text-indigo-600 transition">
                        {relatedPost.title}
                      </h3>
                      <p className="text-sm text-gray-600 line-clamp-2">
                        {relatedPost.excerpt}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Back to Blog Button */}
          <div className="text-center">
            <Link
              to="/blog"
              className="inline-flex items-center bg-indigo-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-indigo-700 transition"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Zurück zur Übersicht
            </Link>
          </div>

        </article>
      </div>
    </>
  )
}

export default BlogDetailPageNew
